import React from 'react';
import { TrendingDown, TrendingUp, Undo2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/format';
import type { Category } from '@/lib/api';
import type { RowDecision, RowPair } from './useOFXReconciliation';
import { EditableDescription } from './EditableDescription';

interface NewEntryRowProps {
  pair: RowPair;
  decision: RowDecision;
  categoriesByType: Record<'income' | 'expense', Category[]>;
  onSetCategoryId: (fitid: string, categoryId: string) => void;
  onSetDescriptionOverride: (fitid: string, value: string | null) => void;
  onRevertOverride?: (fitid: string) => void;
  errorMessage?: string;
}

function compactDate(date: string): string {
  const [, month, day] = date.split('-');
  return `${day}/${month}`;
}

export const NewEntryRow: React.FC<NewEntryRowProps> = ({
  pair,
  decision,
  categoriesByType,
  onSetCategoryId,
  onSetDescriptionOverride,
  onRevertOverride,
  errorMessage,
}) => {
  const { row, fitid } = pair;
  const isIncome = row.type === 'income';
  const candidates = isIncome ? categoriesByType.income : categoriesByType.expense;
  const isOverride = pair.case === 'auto-link' || pair.case === 'ambiguous';
  const effectiveDescription = decision.descriptionOverride ?? row.description;
  const isDescriptionEdited = decision.descriptionOverride !== null;

  const handleDescriptionCommit = (next: string) => {
    onSetDescriptionOverride(fitid, next === row.description ? null : next);
  };

  return (
    <div className="px-4 py-3 animate-fade-in">
      <div className="flex items-center gap-3">
        <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap shrink-0 w-10">
          {compactDate(row.date)}
        </span>
        <span
          className={cn(
            'inline-flex items-center justify-center h-5 w-5 rounded-full shrink-0',
            isIncome ? 'bg-income/12 text-income' : 'bg-expense/12 text-expense',
          )}
          aria-label={isIncome ? 'Receita' : 'Despesa'}
        >
          {isIncome ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
        </span>
        <EditableDescription
          value={effectiveDescription}
          isEdited={isDescriptionEdited}
          onCommit={handleDescriptionCommit}
        />
        <span
          className={cn(
            'text-sm font-medium tabular-nums whitespace-nowrap shrink-0',
            isIncome ? 'text-income' : 'text-expense',
          )}
        >
          {formatCurrency(row.amount)}
        </span>
      </div>

      <div className="mt-2 pl-[3.25rem] flex items-center gap-2">
        <Select
          value={decision.categoryId ?? ''}
          onValueChange={(v) => onSetCategoryId(fitid, v)}
        >
          <SelectTrigger
            className={cn(
              'h-9 text-sm flex-1 min-w-0',
              !decision.categoryId && 'border-border/80 text-muted-foreground',
            )}
            aria-label="Categoria"
          >
            <SelectValue placeholder="Selecione uma categoria" />
          </SelectTrigger>
          <SelectContent>
            {candidates.length === 0 ? (
              <div className="px-2 py-3 text-center">
                <p className="text-xs text-muted-foreground">
                  Nenhuma categoria de {isIncome ? 'receita' : 'despesa'}
                </p>
              </div>
            ) : (
              candidates.map((c) => (
                <SelectItem key={c.id} value={c.id} className="text-sm">
                  {c.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        {isOverride && onRevertOverride && (
          <button
            type="button"
            onClick={() => onRevertOverride(fitid)}
            className="text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 px-2 py-1.5 rounded-md transition-colors flex items-center gap-1 shrink-0"
            title={
              pair.case === 'auto-link'
                ? 'Restaurar vínculo automático'
                : 'Voltar para escolha de vínculo'
            }
          >
            <Undo2 className="h-3 w-3" />
            <span className="hidden sm:inline">Reverter</span>
          </button>
        )}
      </div>

      {errorMessage && (
        <p className="mt-2 pl-[3.25rem] text-xs text-destructive">{errorMessage}</p>
      )}
    </div>
  );
};
